import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navLinks = [
  { label: 'Home', to: { pathname: '/', hash: '#top' } },
  { label: 'Company', to: { pathname: '/', hash: '#company' } },
  { label: 'Management', to: { pathname: '/', hash: '#management' } },
  { label: 'Scan4Loyalty', to: { pathname: '/', hash: '#scan4loyalty' } },
  { label: 'Products', to: { pathname: '/', hash: '#products' } },
  { label: 'Careers', to: { pathname: '/', hash: '#careers' } },
  { label: 'Contact', to: '/contact' },
];

const HEADER_OFFSET_PX = 88;

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const top =
    el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET_PX;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => setIsOpen(false);

  const handleHashNav = (e, to) => {
    if (typeof to === 'string' || !to.hash) return;
    const id = to.hash.replace(/^#/, '');
    if (location.pathname === '/' && id) {
      e.preventDefault();
      navigate(to);
      window.setTimeout(() => scrollToSection(id), 0);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-surface-100/90 backdrop-blur-md shadow-glow border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-[4.25rem] gap-2 sm:gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 min-w-0 shrink"
            aria-label="Latchiya home"
          >
            <span className="inline-flex rounded-md bg-white p-1.5 shrink-0">
              <img
                src="/assets/images/latchiya_std.png"
                alt="Latchiya Software Systems"
                className="h-9 sm:h-11 w-auto object-contain"
              />
            </span>
          </Link>

          <nav
            className="hidden lg:flex items-center gap-2 xl:gap-4 flex-wrap justify-end"
            aria-label="Primary"
          >
            {navLinks.map(item => (
              <Link
                key={item.label}
                to={item.to}
                onClick={e => handleHashNav(e, item.to)}
                className="text-xs xl:text-sm font-medium text-gray-300 hover:text-white transition-colors whitespace-nowrap"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="btn-primary text-xs xl:text-sm py-2 px-3 xl:py-2.5 xl:px-5 whitespace-nowrap"
            >
              Free Pilot
            </Link>
          </nav>

          <button
            type="button"
            className="lg:hidden p-2 rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-controls="mobile-nav"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-white/10 bg-surface-100/95 backdrop-blur"
          >
            <div className="container-custom py-4 flex flex-col gap-1">
              {navLinks.map(item => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={e => {
                    handleHashNav(e, item.to);
                    closeMenu();
                  }}
                  className="py-3 text-base font-medium text-gray-200 hover:text-white border-b border-white/5"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={closeMenu}
                className="btn-primary w-full mt-3 text-center"
              >
                Free Pilot
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
